/**
 * `useActionQueue(actor, source)` (13-09): the Today queue and its everyday verbs.
 *
 * Reads:
 *   - the Action Register rows (13-07 `listActionRows` in the app, synthetic in the preview);
 *   - for Oversight, who completed each Done row (`loadVerifyContext`, four at a time): the
 *     verification cards and "You finished…" need it (ISS-188);
 *   - a refetch on window focus, at most once a minute.
 *
 * The view is `buildToday` (lib/todayQueue): `lensFor(actor).myQueue` + `handedBack` +
 * `myVerifications`, sorted overdue first, then due date, then release date.
 *
 * Writes go through the shell's ONE outbox (13-06 / 13-08), whose commit is
 * `getWriter().commitVerb`: practice mode in dev, so nothing here writes a live row.
 *   tap → `canDoVerb` + `buildVerb` → `outbox.enqueue` → the tick (≈250 ms; 0 under reduced
 *   motion) → hidden → toast with Undo → Sonner `onAutoClose` / `onDismiss` → `outbox.flush`.
 * Cards are hidden through reducer state, never `useOptimistic` (RESEARCH Pattern 1).
 *   - Undo → `outbox.undo` and the card comes back. If the save already happened, it says so and
 *     points at Reopen (Done today), which is its own audited verb.
 *   - 412 → the card comes back with "Changed by someone else — refreshed", and the rows reload.
 *   - A failed save is retried twice; after 3 failed attempts the card shows "Couldn't save" with
 *     Retry, and the entry stays in the outbox.
 *
 * 13-10 adds the verbs that carry judgement:
 *   - `run(…, { immediate: true })`: a regulatory decision. No undo toast; the panel's confirm was
 *     the deliberate step, so the outbox entry is flushed straight after it is enqueued.
 *   - `reject(row, reason)`: one fresh audit read (who completed it, and how many times it was
 *     rejected before), then `reject` with `priorRejections`, so a second rejection escalates (E3).
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { toast } from 'sonner';
import { UNDO_WINDOW_MS } from '../config/actionRegister';
import { canDoVerb } from '../lib/actionAuthority';
import type { AuthorityContext } from '../lib/actionAuthority';
import { VerbError, buildVerb } from '../lib/actionVerbs';
import type { VerbInput } from '../lib/actionVerbs';
import { applyPatch, buildToday, cardRefusalText } from '../lib/todayQueue';
import type { CompleterState, DoneItem, SessionAction, TodayView } from '../lib/todayQueue';
import type { Outbox } from '../lib/outbox';
import type { WriteMode } from '../services/commitAdapter';
import type { IncidentContext } from '../services/actionService';
import type { ActionRow, ActionVerb, Actor } from '../types/actionRegister';

// ---------------------------------------------------------------------------
// The source (Graph + the shell's outbox in the app; synthetic in the preview)
// ---------------------------------------------------------------------------

export interface TodayContext {
  completedByUpn?: string;
  priorRejections: number;
}

export interface TodaySource {
  mode: WriteMode;
  /** The shell's outbox (13-08), bound to the writer for this build. */
  outbox: Outbox;
  listRows: () => Promise<ActionRow[]>;
  /** One audit read: who completed the row (ISS-188) and its prior rejections. */
  loadContext: (row: ActionRow) => Promise<TodayContext>;
  /** The eTag our last commit on an item returned, and when (so a later verb doesn't 412). */
  eTagFor?: (itemId: number) => { eTag: string; at: number } | undefined;
  /** Oversight's patch line: pending Verification Log rows. */
  countPendingVerifications?: () => Promise<number>;
  /**
   * The regulatory panel (13-10): a read-only fetch of the parent Incident's key fields, by its
   * `ParentId` (`INC-<id>`). Absent: the panel says the incident can't be shown here.
   */
  loadIncident?: (parentId: string) => Promise<IncidentContext>;
}

export interface CardNote {
  tone: 'conflict' | 'failed' | 'refused';
  text: string;
  /** `failed`: the entry still in the outbox, for Retry / Don't save. */
  actionId?: string;
}

export interface RunCopy {
  /** The toast line, e.g. "Done: Call Client A". */
  title: string;
  description?: string;
}

export type RunResult = { ok: true; actionId: string } | { ok: false; reason: string };

export interface RunOptions {
  /**
   * Commit at once, with no undo window (a regulatory decision, 13-10): the deliberate step was
   * the panel's confirm. The toast says what was recorded; there is nothing to undo.
   */
  immediate?: boolean;
}

export const TICK_MS = 260;
export const REFETCH_MIN_MS = 60_000;
const MAX_ATTEMPTS = 3;
const COMPLETER_CONCURRENCY = 4;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface LocalRow {
  row: ActionRow;
  /** The server eTag the override was made against: a refetch with a new eTag drops it. */
  baseETag: string;
}

interface State {
  status: 'loading' | 'ready' | 'error';
  error: string | null;
  rows: ActionRow[];
  loadedAt: number;
  local: Record<number, LocalRow>;
  ticking: Record<number, true>;
  hidden: Record<number, true>;
  notes: Record<number, CardNote>;
  doneNotes: Record<number, string>;
  session: SessionAction[];
  completers: Record<number, { eTag: string; state: CompleterState }>;
  pendingVerifications: number | null;
}

type Action =
  | { type: 'loaded'; rows: ActionRow[]; at: number }
  | { type: 'loadFailed'; error: string }
  | { type: 'local'; id: number; row: ActionRow | null; baseETag?: string }
  | { type: 'tick'; id: number }
  | { type: 'hide'; id: number }
  | { type: 'restore'; id: number; note?: CardNote; local?: LocalRow | null }
  | { type: 'committed'; action: SessionAction; local: LocalRow }
  | { type: 'note'; id: number; note: CardNote | null }
  | { type: 'doneNote'; id: number; text: string | null }
  | { type: 'completer'; id: number; eTag: string; state: CompleterState }
  | { type: 'pendingVerifications'; n: number | null };

const INITIAL: State = {
  status: 'loading',
  error: null,
  rows: [],
  loadedAt: 0,
  local: {},
  ticking: {},
  hidden: {},
  notes: {},
  doneNotes: {},
  session: [],
  completers: {},
  pendingVerifications: null,
};

function without<T>(rec: Record<number, T>, id: number): Record<number, T> {
  if (!(id in rec)) return rec;
  const next = { ...rec };
  delete next[id];
  return next;
}

function reducer(s: State, a: Action): State {
  switch (a.type) {
    case 'loaded': {
      // An override survives only while the server row is still the one it was made against.
      const byId = new Map(a.rows.map((r) => [r.id, r]));
      const local: Record<number, LocalRow> = {};
      for (const [k, v] of Object.entries(s.local)) {
        const server = byId.get(Number(k));
        if (server && server.eTag === v.baseETag) local[Number(k)] = v;
      }
      return { ...s, status: 'ready', error: null, rows: a.rows, loadedAt: a.at, local };
    }
    case 'loadFailed':
      return { ...s, status: s.rows.length ? 'ready' : 'error', error: a.error };
    case 'local': {
      if (a.row === null) return { ...s, local: without(s.local, a.id) };
      const base = a.baseETag ?? s.rows.find((r) => r.id === a.id)?.eTag ?? a.row.eTag;
      return { ...s, local: { ...s.local, [a.id]: { row: a.row, baseETag: base } } };
    }
    case 'tick':
      return { ...s, ticking: { ...s.ticking, [a.id]: true }, notes: without(s.notes, a.id) };
    case 'hide':
      return { ...s, ticking: without(s.ticking, a.id), hidden: { ...s.hidden, [a.id]: true } };
    case 'restore': {
      const next: State = { ...s, ticking: without(s.ticking, a.id), hidden: without(s.hidden, a.id) };
      if (a.note) next.notes = { ...s.notes, [a.id]: a.note };
      if (a.local === null) next.local = without(s.local, a.id);
      else if (a.local) next.local = { ...s.local, [a.id]: a.local };
      return next;
    }
    case 'committed':
      return {
        ...s,
        session: [...s.session, a.action],
        local: { ...s.local, [a.action.id]: a.local },
        notes: without(s.notes, a.action.id),
        ticking: without(s.ticking, a.action.id),
        // A committed row leaves (or rejoins) the queue through its new status; nothing stays hidden.
        hidden: without(s.hidden, a.action.id),
      };
    case 'note':
      return { ...s, notes: a.note ? { ...s.notes, [a.id]: a.note } : without(s.notes, a.id) };
    case 'doneNote':
      return { ...s, doneNotes: a.text ? { ...s.doneNotes, [a.id]: a.text } : without(s.doneNotes, a.id) };
    case 'completer':
      return { ...s, completers: { ...s.completers, [a.id]: { eTag: a.eTag, state: a.state } } };
    case 'pendingVerifications':
      return { ...s, pendingVerifications: a.n };
    default:
      return s;
  }
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

/** One tap in flight: what to put back on undo, and what to record on commit. */
interface Pending {
  actionId: string;
  verb: ActionVerb;
  row: ActionRow;
  after: ActionRow;
  /** The override before this tap (restored on undo or failure). */
  prevLocal: LocalRow | undefined;
  at: string;
  attempts: number;
  /** The card stays (accept / reopen): its new state shows at once. */
  inPlace: boolean;
}

export interface ActionQueue {
  status: State['status'];
  error: string | null;
  /** The rows as the page shows them (server rows with this session's changes on top). */
  rows: ActionRow[];
  view: TodayView;
  ticking: ReadonlySet<number>;
  /** Cards changed in place this session (accept / reopen). */
  morphed: ReadonlySet<number>;
  notes: Readonly<Record<number, CardNote>>;
  doneNotes: Readonly<Record<number, string>>;
  pendingVerifications: number | null;
  /** Session actions committed so far (for the dry-run count check and Done today). */
  session: readonly SessionAction[];
  run: (verb: ActionVerb, row: ActionRow, input: VerbInput, copy: RunCopy, ctx?: AuthorityContext, opts?: RunOptions) => RunResult;
  /**
   * E3 from the card (13-10): reads who completed the row and its prior rejections, then rejects.
   * The second rejection's toast says it escalated.
   */
  reject: (row: ActionRow, reason: string) => Promise<RunResult>;
  undo: (actionId: string) => void;
  /** Retry a card whose save failed 3 times; its entry is still in the outbox. */
  retry: (id: number) => void;
  /** Drop a failed save (nothing was written) and put the card back as it was. */
  discard: (id: number) => void;
  reopen: (item: DoneItem) => Promise<void>;
  hide: (id: number) => void;
  restore: (id: number, reason?: string) => void;
  reload: () => void;
}

export interface QueueOptions {
  /** Skip the tick delay (reduced motion: every change is instant). */
  reducedMotion?: boolean;
  /** The clock (the preview and tests may pin it). */
  now?: () => number;
}

export function useActionQueue(actor: Actor | null, source: TodaySource | null, opts: QueueOptions = {}): ActionQueue {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const clock = opts.now ?? Date.now;
  const tickMs = opts.reducedMotion ? 0 : TICK_MS;

  // Mutable bookkeeping that must not re-render: in-flight taps, timers, the load guard.
  const pending = useRef(new Map<string, Pending>());
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  const loadSeq = useRef(0);
  const lastLoad = useRef(0);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const later = useCallback((fn: () => void, ms: number) => {
    const t = setTimeout(() => {
      timers.current.delete(t);
      fn();
    }, ms);
    timers.current.add(t);
  }, []);

  useEffect(() => {
    const live = timers.current;
    return () => {
      live.forEach(clearTimeout);
      live.clear();
    };
  }, []);

  // --- reads -----------------------------------------------------------------

  const reload = useCallback(() => {
    if (!source) return;
    const seq = ++loadSeq.current;
    const startedAt = clock();
    lastLoad.current = startedAt;
    source
      .listRows()
      .then((rows) => {
        if (seq === loadSeq.current) dispatch({ type: 'loaded', rows, at: startedAt });
      })
      .catch((err: unknown) => {
        if (seq === loadSeq.current) {
          dispatch({ type: 'loadFailed', error: err instanceof Error ? err.message : 'The Action Register could not be read.' });
        }
      });
    if (source.countPendingVerifications && actor?.roles.includes('Oversight')) {
      source
        .countPendingVerifications()
        .then((n) => dispatch({ type: 'pendingVerifications', n }))
        .catch(() => dispatch({ type: 'pendingVerifications', n: null }));
    }
  }, [source, actor, clock]);

  useEffect(() => {
    // The first read for this source: the request is made here, its result lands in the reducer.
    reload();
  }, [reload]);

  useEffect(() => {
    const onFocus = () => {
      if (clock() - lastLoad.current >= REFETCH_MIN_MS) reload();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [reload, clock]);

  // The rows as shown: the server's, with this session's overrides on top.
  const rows = useMemo(
    () => state.rows.map((r) => state.local[r.id]?.row ?? r),
    [state.rows, state.local],
  );

  // Who completed each Done row (Oversight: verification cards; everyone: Done today by audit).
  const completerWanted = useMemo(() => {
    if (!actor?.roles.includes('Oversight')) return [];
    return rows.filter((r) => r.status === 'Done' && (r.kind === 'Action' || r.kind === 'Approval'));
  }, [rows, actor]);

  const inflightCompleters = useRef(new Set<string>());
  useEffect(() => {
    if (!source) return;
    const todo = completerWanted.filter((r) => {
      const have = state.completers[r.id];
      const key = `${r.id}|${r.eTag}`;
      return (!have || have.eTag !== r.eTag) && !inflightCompleters.current.has(key);
    });
    if (todo.length === 0) return;
    let next = 0;
    const worker = async () => {
      while (next < todo.length) {
        const row = todo[next++];
        const key = `${row.id}|${row.eTag}`;
        inflightCompleters.current.add(key);
        try {
          const ctx = await source.loadContext(row);
          dispatch({ type: 'completer', id: row.id, eTag: row.eTag, state: { status: 'ready', completedByUpn: ctx.completedByUpn } });
        } catch {
          dispatch({ type: 'completer', id: row.id, eTag: row.eTag, state: { status: 'error' } });
        } finally {
          inflightCompleters.current.delete(key);
        }
      }
    };
    for (let i = 0; i < Math.min(COMPLETER_CONCURRENCY, todo.length); i += 1) void worker();
  }, [completerWanted, source, state.completers]);

  const completers = useMemo(() => {
    const out: Record<number, CompleterState> = {};
    for (const r of completerWanted) {
      const have = state.completers[r.id];
      out[r.id] = have && have.eTag === r.eTag ? have.state : { status: 'loading' };
    }
    return out;
  }, [completerWanted, state.completers]);

  const hiddenSet = useMemo(() => new Set(Object.keys(state.hidden).map(Number)), [state.hidden]);

  // The view's clock is the last read time: stable between renders, fresh on every refetch.
  const viewNow = state.loadedAt || 0;
  const view = useMemo<TodayView>(() => {
    if (!actor) return { queue: [], others: [], done: [] };
    return buildToday({
      rows,
      actor,
      now: new Date(viewNow || Date.parse('2000-01-01T00:00:00Z')),
      completers,
      session: state.session,
      hidden: hiddenSet,
    });
  }, [actor, rows, completers, state.session, hiddenSet, viewNow]);

  // --- writes ----------------------------------------------------------------

  const commit = useCallback(
    async (actionId: string): Promise<void> => {
      const p = pending.current.get(actionId);
      if (!p || !source || !actor) return;
      const outcome = await source.outbox.flush(actionId);
      if (!pending.current.has(actionId)) return; // undone meanwhile
      switch (outcome) {
        case 'committed':
        case 'missing': // committed by flushAll (tab hidden) or a replay; an undone tap is no longer pending
        case 'taken': {
          // 'taken': another tab holds and commits it.
          pending.current.delete(actionId);
          const eTag = source.eTagFor?.(p.row.id)?.eTag;
          const after = eTag ? { ...p.after, eTag } : p.after;
          const base = stateRef.current.rows.find((r) => r.id === p.row.id)?.eTag ?? p.row.eTag;
          dispatch({
            type: 'committed',
            action: { actionId, id: p.row.id, verb: p.verb, title: p.row.title, at: p.at, byUpn: actor.upn, row: after },
            local: { row: after, baseETag: base },
          });
          return;
        }
        case 'conflict': {
          pending.current.delete(actionId);
          source.outbox.undo(actionId); // a 412 wrote nothing: drop the entry (D-13-06-6)
          dispatch({
            type: 'restore',
            id: p.row.id,
            local: p.prevLocal ?? null,
            note: { tone: 'conflict', text: 'Changed by someone else — refreshed' },
          });
          reload();
          return;
        }
        case 'wrong-user': {
          pending.current.delete(actionId);
          dispatch({
            type: 'restore',
            id: p.row.id,
            local: p.prevLocal ?? null,
            note: { tone: 'refused', text: "You're signed in as someone else now, so this wasn't saved." },
          });
          return;
        }
        case 'failed':
        case 'blocked':
        default: {
          p.attempts += 1;
          if (p.attempts < MAX_ATTEMPTS) {
            later(() => void commit(actionId), 1500 * p.attempts);
            return;
          }
          dispatch({
            type: 'restore',
            id: p.row.id,
            local: p.prevLocal ?? null,
            note: { tone: 'failed', text: "Couldn't save", actionId },
          });
        }
      }
    },
    [source, actor, later, reload],
  );

  const undo = useCallback(
    (actionId: string) => {
      const p = pending.current.get(actionId);
      if (!source || !p) return;
      const undone = source.outbox.undo(actionId);
      if (!undone) {
        toast(p.verb === 'done' ? 'Already saved. Reopen it from Done today.' : 'Already saved.', {
          className: 'qms-toast',
        });
        return;
      }
      // The outbox also undid any later tap on the same card (D-13-06-4); put the card back as it
      // was before the first of them.
      const sameItem = [...pending.current.values()].filter((x) => x.row.id === p.row.id);
      const ids = new Set(sameItem.filter((x) => !source.outbox.entries().some((e) => e.actionId === x.actionId)).map((x) => x.actionId));
      ids.add(actionId);
      for (const id of ids) pending.current.delete(id);
      dispatch({ type: 'restore', id: p.row.id, local: p.prevLocal ?? null });
    },
    [source],
  );

  const run = useCallback(
    (
      verb: ActionVerb,
      row: ActionRow,
      input: VerbInput,
      copy: RunCopy,
      ctx: AuthorityContext = {},
      runOpts: RunOptions = {},
    ): RunResult => {
      if (!source || !actor) return { ok: false, reason: 'not-ready' };
      const s = stateRef.current;
      // A double tap, or a save still outstanding. The ref is checked as well as the state, because
      // two taps in one frame both run before React has re-rendered.
      const leaving = [...pending.current.values()].some((x) => x.row.id === row.id && !x.inPlace);
      if (leaving || s.ticking[row.id] || s.hidden[row.id] || s.notes[row.id]?.tone === 'failed') {
        return { ok: false, reason: 'busy' };
      }
      const sameVerbPending = [...pending.current.values()].some((x) => x.row.id === row.id && x.verb === verb);
      if (sameVerbPending) return { ok: false, reason: 'busy' };
      const allowed = canDoVerb(verb, row, actor, ctx);
      if (!allowed.ok) return { ok: false, reason: allowed.reason };

      const actedAt = new Date(clock()).toISOString();
      const known = source.eTagFor?.(row.id);
      const eTag = known && known.at >= s.loadedAt ? known.eTag : row.eTag;
      let built;
      try {
        built = buildVerb(verb, row, actor, input, actedAt);
      } catch (err) {
        if (err instanceof VerbError) return { ok: false, reason: err.code };
        throw err;
      }
      if (Object.keys(built.patch).length === 0) return { ok: false, reason: 'no-change' };

      const actionId = source.outbox.enqueue({
        itemId: row.id,
        eTag,
        verb,
        patch: built.patch,
        audit: built.audit,
        ownerUpn: actor.upn,
        actedAt,
      });
      const inPlace = verb === 'accept' || verb === 'reopen';
      const after = applyPatch(row, built.patch);
      pending.current.set(actionId, {
        actionId,
        verb,
        row,
        after,
        prevLocal: s.local[row.id],
        at: actedAt,
        attempts: 0,
        inPlace,
      });

      if (inPlace) {
        dispatch({ type: 'local', id: row.id, row: after });
      } else {
        dispatch({ type: 'tick', id: row.id });
        later(() => {
          if (pending.current.has(actionId) || stateRef.current.ticking[row.id]) dispatch({ type: 'hide', id: row.id });
        }, tickMs);
      }

      if (runOpts.immediate) {
        // A regulatory decision: the confirm was the deliberate step. No undo; commit now.
        toast(copy.title, { className: 'qms-toast', description: copy.description });
        void commit(actionId);
        return { ok: true, actionId };
      }
      if (!source.outbox.durable) {
        // No storage for an undo window: save straight away, and say so.
        toast(copy.title, { className: 'qms-toast', description: 'Saved straight away. Reopen from Done today if it was a mistake.' });
        void commit(actionId);
        return { ok: true, actionId };
      }
      toast(copy.title, {
        className: 'qms-toast',
        description: copy.description,
        duration: UNDO_WINDOW_MS,
        action: { label: 'Undo', onClick: () => undo(actionId) },
        onAutoClose: () => void commit(actionId),
        onDismiss: () => void commit(actionId),
      });
      return { ok: true, actionId };
    },
    [source, actor, clock, later, tickMs, commit, undo],
  );

  const retry = useCallback(
    (id: number) => {
      const note = stateRef.current.notes[id];
      if (!note?.actionId) return;
      const p = pending.current.get(note.actionId);
      if (!p) return;
      p.attempts = MAX_ATTEMPTS - 1; // one more try; it shows "Couldn't save" again if it fails
      dispatch({ type: 'note', id, note: null });
      if (p.inPlace) dispatch({ type: 'local', id, row: p.after });
      else dispatch({ type: 'hide', id });
      void commit(note.actionId);
    },
    [commit],
  );

  const discard = useCallback(
    (id: number) => {
      const note = stateRef.current.notes[id];
      if (!note?.actionId || !source) return;
      const p = pending.current.get(note.actionId);
      if (source.outbox.undo(note.actionId) && p) pending.current.delete(note.actionId);
      dispatch({ type: 'note', id, note: null });
    },
    [source],
  );

  const reopen = useCallback(
    async (item: DoneItem) => {
      if (!source || !actor) return;
      const row = item.row;
      let completedByUpn = item.sessionBy;
      if (!completedByUpn) {
        // ISS-188, the Today limb: the completer comes from the audit trail.
        dispatch({ type: 'doneNote', id: row.id, text: cardRefusalText('checking') });
        try {
          completedByUpn = (await source.loadContext(row)).completedByUpn;
        } catch {
          dispatch({ type: 'doneNote', id: row.id, text: cardRefusalText('unreadable') });
          return;
        }
      }
      const result = run(
        'reopen',
        row,
        {},
        { title: `Reopened: ${row.title}`, description: 'Reopening is its own audited change.' },
        { completedByUpn },
      );
      dispatch({ type: 'doneNote', id: row.id, text: result.ok ? null : cardRefusalText(result.reason) });
    },
    [source, actor, run],
  );

  const reject = useCallback(
    async (row: ActionRow, reason: string): Promise<RunResult> => {
      if (!source || !actor) return { ok: false, reason: 'not-ready' };
      const note = reason.trim();
      if (!note) return { ok: false, reason: 'reason-required' };
      let ctx;
      try {
        // One fresh audit read: the completer (ISS-188) and the prior rejections (D-13-00-3).
        ctx = await source.loadContext(row);
      } catch {
        return { ok: false, reason: 'unreadable' };
      }
      const second = ctx.priorRejections >= 1;
      return run(
        'reject',
        row,
        { note, priorRejections: ctx.priorRejections },
        second
          ? { title: 'Returned to owner · escalated to their manager', description: `${row.reference}: rejected before, so their manager is told too.` }
          : { title: `Returned to its owner: ${row.title}`, description: 'With your reason. If it comes back a second time, their manager is told too.' },
        { completedByUpn: ctx.completedByUpn },
      );
    },
    [source, actor, run],
  );

  const hide = useCallback((id: number) => dispatch({ type: 'hide', id }), []);
  const restore = useCallback(
    (id: number, reason?: string) =>
      dispatch({ type: 'restore', id, note: reason ? { tone: 'refused', text: reason } : undefined }),
    [],
  );

  const ticking = useMemo(() => new Set(Object.keys(state.ticking).map(Number)), [state.ticking]);

  // Cards that changed in place this session (accept / reopen): they get the soft teal morph.
  const morphed = useMemo(() => {
    const out = new Set<number>();
    for (const [k, v] of Object.entries(state.local)) {
      const server = state.rows.find((r) => r.id === Number(k));
      if (v.row.status === 'Accepted' && server && server.status !== 'Accepted') out.add(Number(k));
    }
    return out;
  }, [state.local, state.rows]);

  return {
    morphed,
    status: state.status,
    error: state.error,
    rows,
    view,
    ticking,
    notes: state.notes,
    doneNotes: state.doneNotes,
    pendingVerifications: state.pendingVerifications,
    session: state.session,
    run,
    reject,
    undo,
    retry,
    discard,
    reopen,
    hide,
    restore,
    reload,
  };
}
